/**
 * 手動イベントの最小セット定義（GF-1888 / GF-1896）。
 *
 * ⚠️ Autocapture 主軸の運用方針：
 *   - クリック / ページ遷移 / フォーム submit は Autocapture に任せ、ここに追加しない。
 *   - ここに定義するのは「Autocapture で取れない / 信頼性が必要なドメインイベント」だけ。
 *   - 新規追加は docs（posthog.md）の承認フローに従うこと。「念のため」の追加は禁止。
 *
 * イベント名（EVENT_NAMES の値）は PostHog のイベント識別子として snake_case、プロパティキーは GA4のプロパティに合わせて camelCase で定義する。
 */
export const EVENT_NAMES = {
  /** 購入完了。金額・商品ID・注文ID は Autocapture では取れないため手動送信する。 */
  purchaseCompleted: 'purchase_completed',
  /**
   * 購入明細（1商品=1件）。経路別の商品分析（構成比・経路別アイテム収益）を明細スカラーで行うため、
   * purchase_completed（注文スコープ）とは別に明細スコープで手動送信する。
   */
  purchaseItem: 'purchase_item',
  /** デザインツールのキャンバス変形（移動/拡大/回転）。Fabric.js の canvas は DOM クリックでないため Autocapture で取れず手動送信する（GF-2121）。 */
  designCanvasTransform: 'design_tool_canvas_transform',
  /** デザインツールで素材をキャンバスに配置（=デザイン着手の正式シグナル）。canvas 処理のため Autocapture で取れず手動送信する（GF-2121）。 */
  designMaterialPlaced: 'design_tool_material_placed',
  /** デザインツール離脱サマリ。離脱位置(exit_phase)・滞在時間・着手有無を1セッション1回だけ送る。即離脱と諦め離脱の切り分け用（GF-2121）。 */
  designToolExit: 'design_tool_exit',
  /** カート投入。GA4 の add_to_cart と同時に送り、経路別ファネル（デザイン完了など）を PostHog で組むために手動送信する。 */
  addToCart: 'add_to_cart',
  /** コマクリック。作品別のコマ関心度を見る。 */
  materialClick: 'material_click',
  /**
   * カート「この作品でさらにグッズをつくる」。どの作品からの買い周りが効いているかを見る。作品情報が本体のため手動送信する。
   * dual の他イベントは `*_click` だが、本イベントは同じカート画面の隣のボタン（`click_cart_redesign`）を含む
   * GA4 カートクリック群の `click_*` に合わせる。GTM のトリガーもこの名前で稼働中。
   */
  cartGoWorkDetail: 'click_cart_go_work_detail',
  /** TOPピックアップバナークリック（集英社要望バナーの効果測定）。 */
  pickupBannerClick: 'pickup_banner_click',
  /** TOPヒーロー探索導線クリック（入口別の下流成果を見る）。 */
  exploreEntryClick: 'explore_entry_click',
  /** SNS/メール登録の開始（方法ボタン押下・OAuthリダイレクト前）。方法別・導線別の開始→完了率を測るため手動送信する。 */
  signUpStart: 'sign_up_start',
  /** 登録完了（サーバー確定後のフロント着地時に1回）。GA4推奨イベント。method/flowOrigin 付き。 */
  signUp: 'sign_up',
  /** ログイン成功（着地時に1回）。GA4推奨イベント。method/flowOrigin 付き。 */
  login: 'login',
  /** アカウント連携の追加確定。連携の量・導線・連携者セグメントを見るため手動送信する。 */
  accountLinkComplete: 'account_link_complete',
  /** アカウント連携の解除確定。 */
  accountUnlinkComplete: 'account_unlink_complete',
  /** LINE連携促進ボタンのクリック（購入完了/マイページ）。誘導ボタンの効果測定用。 */
  lineLinkClick: 'line_link_click',
  /** LINE友だち追加促進ボタンのクリック（連携済み・友だち未登録時）。 */
  lineFriendClick: 'line_friend_click',
} as const;

export type EventName = (typeof EVENT_NAMES)[keyof typeof EVENT_NAMES];

/** 認証方法。GA4推奨イベント sign_up/login の標準パラメータのため改名しない。 */
export const AUTH_METHODS = ['email', 'google', 'line'] as const;
export type AuthMethod = (typeof AUTH_METHODS)[number];

/** 認証・連携・LINE誘導イベントの発生導線／表示場所（camelCase）。着地の誤ラベルを防ぐため入口の値を全画面遷移で引き継ぐ。 */
export const AUTH_FLOW_ORIGINS = [
  'signup',
  'login',
  'purchaseAuth',
  'guestConversion',
  'loginRedirect',
  'signupRedirect',
  'signupAutoLink',
  'purchaseComplete',
  'myPage',
] as const;
export type AuthFlowOrigin = (typeof AUTH_FLOW_ORIGINS)[number];

/** URL 由来など未検証の文字列を AuthMethod として受けてよいか判定する。不正値は計測に流さないためのガード。 */
export const isAuthMethod = (value: string | null | undefined): value is AuthMethod =>
  AUTH_METHODS.some((method) => method === value);

/** URL 由来など未検証の文字列を AuthFlowOrigin として受けてよいか判定する。 */
export const isAuthFlowOrigin = (value: string | null | undefined): value is AuthFlowOrigin =>
  AUTH_FLOW_ORIGINS.some((flowOrigin) => flowOrigin === value);

/**
 * 各手動イベントのプロパティスキーマ。キーは {@link EVENT_NAMES} の「値」。
 * PII（メール / 住所 / 電話 / カード番号）は絶対に含めない。
 */
export type EventProperties = {
  purchase_completed: {
    orderId: string;
    /** 税込合計金額 */
    amount: number;
    productIds: string[];
    /** 購入商品の合計点数 */
    quantity: number;
    /** 'credit' | 'paypay' 等の決済手段 */
    paymentMethod: string;
    /** 会員購入かゲスト購入か */
    isGuest: boolean;
    // 経路（cartRoute 等）は明細スコープのため purchase_item 側に持たせ、注文スコープのここには載せない
    /** 注文内の distinct な作品 ID */
    workIds: number[];
  };
  purchase_item: {
    /** 注文スコープの purchase_completed と突き合わせるための注文ID */
    orderId: string;
    /** 商品識別子（SKU コード）。商品別集計のブレイクダウンキー */
    sku: string;
    /** 商品名（例: スタンダードTシャツ）。売れ筋商品の可読ラベル */
    name: string;
    /** カラー */
    color: string;
    /** サイズ（template 商品などサイズが無い場合は空文字） */
    size: string;
    /** 商品タイプ（design_tool / template 等） */
    productType: string;
    /** この明細の小計（税込・クーポン値引き後）。注文スコープの purchase_completed.amount と基準を揃える */
    amount: number;
    /** この明細の購入点数 */
    quantity: number;
    // 経路は明細スカラーで持ち、そのまま breakdown する（配列にしない）。解決不能時は 'unknown'
    /** この明細の cartRoute（最終経路） */
    cartRoute: string;
    /** この明細の entryRoute（元経路） */
    entryRoute: string;
    /** みんなのデザイン起点なら true */
    basedOnUserDesign: boolean;
    /** シェアURL流入なら true */
    fromShareUrl: boolean;
    /** 作品名 */
    workName: string;
    /** 印刷エリア名の配列（例: ['前面', '背面']）。どの面に作成したかの分析用 */
    prints: string[];
    /** 作品 ID（解決できた明細のみ付与） */
    workId?: number;
  };
  sign_up_start: { method: AuthMethod; flowOrigin: AuthFlowOrigin };
  sign_up: { method: AuthMethod; flowOrigin: AuthFlowOrigin };
  login: { method: AuthMethod; flowOrigin: AuthFlowOrigin };
  // method を google/line に絞ることで person property linkedProviders と語彙を揃える
  account_link_complete: { method: 'google' | 'line'; flowOrigin: 'signupAutoLink' | 'purchaseComplete' | 'myPage' };
  account_unlink_complete: { method: 'google' | 'line'; flowOrigin: 'myPage' };
  line_link_click: { flowOrigin: 'purchaseComplete' | 'myPage' };
  line_friend_click: { flowOrigin: 'purchaseComplete' | 'myPage' };
  design_tool_canvas_transform: {
    /** 直前の変形種別。拡大と縮小は分けて記録する */
    transformType: 'move' | 'scale_up' | 'scale_down' | 'rotate';
    productId: number;
    partId: number;
    /** canvas 上のコマに紐づく素材ID */
    materialId: number;
    /** 作品ID（作品別分析用） */
    workId?: number;
    user_id: number;
  };
  design_tool_material_placed: {
    productId: number;
    partId: number;
    materialId: number;
    workId?: number;
    user_id: number;
  };
  design_tool_exit: {
    /** 離脱時点で到達していた最深フェーズ */
    exitPhase: 'init' | 'before_first_action' | 'editing' | 'preview' | 'to_cart';
    /** 離脱までに一度でもコマを配置したか（削除で0になっても true 維持） */
    hasAddedMaterial: boolean;
    /** /design 到達〜離脱までの滞在ms（performance.now 差分） */
    dwellTimeMs: number;
    workId?: number;
    user_id: number;
    /** 前回セッションの未送信を localStorage から救出して送ったか（モバイルkill等の取りこぼし回収） */
    isRecovered?: boolean;
  };
  add_to_cart: {
    /** price * quantity の合計 */
    value: number;
    productIds: string[];
    /** カート投入点数の合計 */
    quantity: number;
    /** 会員投入かゲスト投入か */
    isGuest: boolean;
    /** cartRoute（"designed" 等）。経路別ファネルの起点にする */
    cartRoute: string;
    /** entryRoute（元経路） */
    entryRoute: string;
    /** みんなのデザイン起点なら true */
    basedOnUserDesign: boolean;
    /** シェアURL流入なら true */
    fromShareUrl: boolean;
    /** 作品ID（作品別のカート投入→購入ファネル分析用。解決できた場合のみ付与） */
    workId?: number;
    /** 作品名（解決できた場合のみ付与） */
    workName?: string;
  };
  material_click: {
    materialId: number;
    materialName: string;
    /** 作品ID（作品別のコマ関心度分析用） */
    workId: number;
    workName: string;
    user_id: number;
  };
  click_cart_go_work_detail: {
    /** 遷移先の作品ID（作品別の買い周り成果分析用） */
    workId: number;
    workName: string;
    user_id: number;
  };
  pickup_banner_click: {
    bannerId: number;
    /** 掲載枠の並び順（0始まり） */
    position: number;
    user_id: number;
  };
  explore_entry_click: {
    /** どの探索導線から入ったか */
    exploreMode: 'works' | 'userDesigns';
    user_id: number;
  };
};
